import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";
import Login from "./Login";
import Admin from "./Admin";

const POLL_INTERVAL = 20_000;
const FLEET_API = "/api/track/vehicles";

// ── Status taxonomy ──
//
// Intangles raw status.state values we've observed:
//   MOVING, IDLING, CHARGING, STOPPED, PARKED, OFFLINE
// We collapse them into the three buckets the user wants to highlight:
//   key-off (OFFLINE / PARKED — engine cut, ignition off)
//   idling (IDLING / STOPPED — key on, no motion)
//   charging (CHARGING)
//   moving (MOVING — implicit "all good")

const STATE_BUCKETS = {
  MOVING: "moving",
  IDLING: "idling",
  STOPPED: "idling",
  CHARGING: "charging",
  PARKED: "keyoff",
  OFFLINE: "keyoff",
};

function bucketOf(state) {
  return STATE_BUCKETS[String(state || "").toUpperCase()] || "unknown";
}

const BUCKET_LABEL = {
  moving: "Moving",
  idling: "Idling",
  charging: "Charging",
  keyoff: "Key Off",
  unknown: "Unknown",
};

const BUCKET_COLOR = {
  moving: "#2dc4b3",
  idling: "#e0a838",
  charging: "#3578e5",
  keyoff: "#898989",
  unknown: "#898989",
};

// ── Map marker icons ──
//
// Same dot style as vehicle.eim.digital: a 14×14 coloured circle with the
// EIM-dark border. One icon per (bucket, isSelected) pair, cached.

const ICON_CACHE = {};
function iconFor(bucket, isSelected) {
  const key = `${bucket}-${isSelected ? "sel" : "x"}`;
  if (ICON_CACHE[key]) return ICON_CACHE[key];
  const color = BUCKET_COLOR[bucket] || "#898989";
  const cls = isSelected ? "selected" : "";
  const icon = L.divIcon({
    className: "track-marker",
    html: `<div class="marker-dot ${cls}" style="background:${color}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
  ICON_CACHE[key] = icon;
  return icon;
}

function fmtTime(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts));
  if (isNaN(d.getTime())) return "—";
  const now = Date.now();
  const ageS = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (ageS < 60) return `${ageS}s ago`;
  if (ageS < 3600) return `${Math.round(ageS / 60)}m ago`;
  if (ageS < 86_400) return `${Math.round(ageS / 3600)}h ago`;
  return d.toLocaleString();
}

// ── Booking helpers ──
//
// SB portal stores DATETIME values in UTC; server hands us an ISO string
// with a Z suffix in next_booking.start_time_utc. We format times in IST
// and bucket by hours-until-start for the filter chips.

const IST_TIME_FMT = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});
const IST_DATETIME_FMT = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

function bookingMs(nb) {
  if (!nb || !nb.start_time_utc) return null;
  const t = Date.parse(nb.start_time_utc);
  return Number.isFinite(t) ? t : null;
}

function bookingHoursUntil(nb) {
  const ms = bookingMs(nb);
  if (ms == null) return null;
  return (ms - Date.now()) / 3600000;
}

function bookingBucket(nb) {
  const h = bookingHoursUntil(nb);
  if (h == null || h < 0) return null;
  if (h <= 1)  return "1h";
  if (h <= 3)  return "3h";
  if (h <= 6)  return "6h";
  if (h <= 12) return "12h";
  return "later";
}

function fmtBookingShortIST(nb) {
  const ms = bookingMs(nb);
  if (ms == null) return null;
  return IST_TIME_FMT.format(new Date(ms));
}

function fmtBookingFullIST(nb) {
  const ms = bookingMs(nb);
  if (ms == null) return null;
  return IST_DATETIME_FMT.format(new Date(ms)) + " IST";
}

function fmtBookingCountdown(nb) {
  const h = bookingHoursUntil(nb);
  if (h == null) return null;
  if (h < 0) return "now";
  const totalMin = Math.round(h * 60);
  if (totalMin < 60) return `in ${totalMin}m`;
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return mm ? `in ${hh}h ${mm}m` : `in ${hh}h`;
}

const BOOKING_BUCKETS = [
  { value: "1h",    label: "≤ 1h" },
  { value: "3h",    label: "≤ 3h" },
  { value: "6h",    label: "≤ 6h" },
  { value: "12h",   label: "≤ 12h" },
  { value: "later", label: "Later" },
];

function FlyTo({ position, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    map.flyTo(position, zoom ?? map.getZoom(), { duration: 0.8 });
  }, [position, zoom, map]);
  return null;
}

function Dashboard({ user, onLogout }) {
  const [snapshot, setSnapshot] = useState({ vehicles: [], fetchedAt: 0, pollError: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [bucketFilter, setBucketFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [bookingFilter, setBookingFilter] = useState(() => new Set()); // empty = no booking filter
  const [tab, setTab] = useState("map"); // 'map' | 'pick'
  const [mapMode, setMapMode] = useState("light"); // 'light' | 'satellite'
  const [flyTarget, setFlyTarget] = useState(null);
  const timerRef = useRef(null);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await fetch(FLEET_API, { credentials: "include" });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSnapshot(data);
      setError("");
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchVehicles();
    timerRef.current = setInterval(fetchVehicles, POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchVehicles]);

  const vehicles = useMemo(() => snapshot.vehicles || [], [snapshot.vehicles]);
  const counts = useMemo(() => {
    const c = { all: 0, moving: 0, idling: 0, charging: 0, keyoff: 0, unknown: 0 };
    for (const v of vehicles) {
      c.all++;
      c[bucketOf(v.state)]++;
    }
    return c;
  }, [vehicles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (bucketFilter !== "all" && bucketOf(v.state) !== bucketFilter) return false;
      if (bookingFilter.size > 0) {
        const b = bookingBucket(v.next_booking);
        if (!b || !bookingFilter.has(b)) return false;
      }
      if (!q) return true;
      const hay = `${v.plate || ""} ${v.tag || ""} ${v.company_name || ""} ${v.model || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [vehicles, search, bucketFilter, bookingFilter]);

  const bookingCounts = useMemo(() => {
    const c = {};
    for (const v of vehicles) {
      const b = bookingBucket(v.next_booking);
      if (b) c[b] = (c[b] || 0) + 1;
    }
    return c;
  }, [vehicles]);
  const bookingTotal = useMemo(
    () => vehicles.reduce((n, v) => n + (v.next_booking ? 1 : 0), 0),
    [vehicles]
  );

  const toggleBookingFilter = useCallback((val) => {
    setBookingFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  }, []);

  // Default map view = mean of selected fleet, fall back to Mumbai.
  const defaultCenter = useMemo(() => {
    const ps = vehicles.filter((v) => v.lat != null && v.lng != null);
    if (!ps.length) return [19.07, 72.87];
    let lat = 0, lng = 0;
    for (const v of ps) { lat += v.lat; lng += v.lng; }
    return [lat / ps.length, lng / ps.length];
  }, [vehicles]);

  return (
    <div className="track-shell">
      <header className="track-topbar">
        <div className="track-brand">
          <img src="/eim_logo.svg" alt="EIM" className="track-logo" />
          <div className="track-title">
            EIM <span className="accent">·</span> Live Track
          </div>
        </div>
        <nav className="track-tabs">
          <button className={tab === "map" ? "active" : ""} onClick={() => setTab("map")}>
            Map
          </button>
          {user.admin && (
            <button className={tab === "pick" ? "active" : ""} onClick={() => setTab("pick")}>
              Assign
            </button>
          )}
        </nav>
        <div className="track-user">
          <span className="track-user-name">{user.display_name || user.email}</span>
          <button className="track-logout" onClick={onLogout}>Logout</button>
        </div>
      </header>

      {tab === "pick" ? (
        <Admin />
      ) : (
        <div className="track-body">
          <aside className="track-sidebar">
            <div className="track-statbar">
              {["all", "moving", "idling", "charging", "keyoff"].map((b) => (
                <button
                  key={b}
                  className={`track-stat ${bucketFilter === b ? "active" : ""}`}
                  onClick={() => setBucketFilter(b)}
                  style={{
                    "--accent": b === "all" ? "#01363F" : BUCKET_COLOR[b],
                  }}
                >
                  <span className="num">{counts[b] || 0}</span>
                  <span className="lbl">{b === "all" ? "Total" : BUCKET_LABEL[b]}</span>
                </button>
              ))}
            </div>

            <div className="track-search">
              <input
                placeholder="Search plate / fleet / tag…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {bookingTotal > 0 && (
              <div className="track-booking-filter" title="Filter by upcoming slot booking">
                <span className="bf-label">📅 Booking in</span>
                {BOOKING_BUCKETS.map((b) => {
                  const n = bookingCounts[b.value] || 0;
                  if (n === 0) return null;
                  const active = bookingFilter.has(b.value);
                  return (
                    <button
                      key={b.value}
                      className={`bf-chip ${active ? "active" : ""} bucket-${b.value}`}
                      onClick={() => toggleBookingFilter(b.value)}
                    >
                      {b.label} <em>{n}</em>
                    </button>
                  );
                })}
                {bookingFilter.size > 0 && (
                  <button className="bf-clear" onClick={() => setBookingFilter(new Set())}>
                    clear
                  </button>
                )}
              </div>
            )}

            <div className="track-meta">
              <span>
                {loading ? "Loading…" :
                  snapshot.pollError ? <span className="err">Upstream: {snapshot.pollError}</span> :
                  `Updated ${fmtTime(snapshot.fetchedAt)}`}
              </span>
              <span className="dot" />
            </div>

            <div className="track-list">
              {error && <div className="track-error">{error}</div>}
              {!loading && filtered.length === 0 && (
                <div className="track-empty">
                  {vehicles.length === 0
                    ? (user.admin
                        ? <>No vehicles assigned to you yet.<button className="link" onClick={() => setTab("pick")}>Open “Assign” to set one up →</button></>
                        : <>No vehicles have been assigned to you yet. Ask an admin to assign your fleet.</>)
                    : "No vehicles match this filter."}
                </div>
              )}
              {filtered.map((v) => {
                const bucket = bucketOf(v.state);
                const sel = v.id === selectedId;
                return (
                  <div
                    key={v.id || v.plate}
                    className={`track-row ${sel ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedId(v.id);
                      if (v.lat != null && v.lng != null) setFlyTarget([v.lat, v.lng, 14]);
                    }}
                  >
                    <div className="row-head">
                      <span className="plate">{v.plate || v.tag || "—"}</span>
                      {v.next_booking && (
                        <span
                          className={`pill booking-pill bucket-${bookingBucket(v.next_booking) || "later"}`}
                          title={`Slot ${v.next_booking.slot_code || ""} · ${fmtBookingFullIST(v.next_booking)}${v.next_booking.station_name ? ` · ${v.next_booking.station_name}` : ""} (${fmtBookingCountdown(v.next_booking)})`}
                        >
                          📅 {fmtBookingShortIST(v.next_booking)}
                        </span>
                      )}
                      <span className={`pill pill-${bucket}`} style={{ background: BUCKET_COLOR[bucket] }}>
                        {BUCKET_LABEL[bucket]}
                      </span>
                    </div>
                    <div className="row-meta">
                      <span>{v.company_name || "—"}</span>
                      <span>{v.model || ""}</span>
                    </div>
                    <div className="row-stats">
                      <span>
                        <b>{v.soc != null ? `${v.soc}%` : "—"}</b>
                        <em>SOC</em>
                      </span>
                      <span>
                        <b>{v.speed_kmh != null ? `${v.speed_kmh}` : "—"}</b>
                        <em>km/h</em>
                      </span>
                      <span>
                        <b>{fmtTime(v.gps_ts || v.state_ts)}</b>
                        <em>seen</em>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          <main className="track-map-wrap">
            <div className="track-mapmode">
              <button
                className={mapMode === "light" ? "active" : ""}
                onClick={() => setMapMode("light")}
              >
                Map
              </button>
              <button
                className={mapMode === "satellite" ? "active" : ""}
                onClick={() => setMapMode("satellite")}
              >
                Satellite
              </button>
            </div>
            <MapContainer
              center={defaultCenter}
              zoom={6}
              zoomControl={false}
              style={{ width: "100%", height: "100%" }}
            >
              {mapMode === "light" ? (
                <TileLayer
                  key="light"
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2s9p_1_0dc8e7ba31e2ef9b87fd2a2d"
                  attribution='&copy; OpenStreetMap, &copy; CARTO'
                  maxZoom={20}
                />
              ) : (
                <TileLayer
                  key="satellite"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="&copy; Esri"
                  maxZoom={19}
                />
              )}
              <ZoomControl position="topright" />
              {flyTarget && <FlyTo position={[flyTarget[0], flyTarget[1]]} zoom={flyTarget[2]} />}
              {filtered.map((v) => {
                if (v.lat == null || v.lng == null) return null;
                return (
                  <Marker
                    key={v.id || v.plate}
                    position={[v.lat, v.lng]}
                    icon={iconFor(bucketOf(v.state), v.id === selectedId)}
                    eventHandlers={{
                      click: () => {
                        setSelectedId(v.id);
                        setFlyTarget([v.lat, v.lng, 13]);
                      },
                    }}
                  />
                );
              })}
            </MapContainer>

            {selectedId && (() => {
              const v = vehicles.find((x) => x.id === selectedId);
              if (!v) return null;
              const bucket = bucketOf(v.state);
              return (
                <div className="track-detail-card">
                  <button className="close" onClick={() => setSelectedId(null)}>×</button>
                  <div className="detail-head">
                    <div className="detail-plate">{v.plate || v.tag}</div>
                    <span className="pill" style={{ background: BUCKET_COLOR[bucket] }}>
                      {BUCKET_LABEL[bucket]}
                    </span>
                  </div>
                  <div className="detail-sub">{v.company_name || "—"} · {v.model || ""}</div>
                  {v.next_booking && (
                    <div className={`detail-booking bucket-${bookingBucket(v.next_booking) || "later"}`}>
                      <span className="db-icon">📅</span>
                      <div className="db-body">
                        <div className="db-time">
                          {fmtBookingFullIST(v.next_booking)}
                          <span className="db-countdown">{fmtBookingCountdown(v.next_booking)}</span>
                        </div>
                        <div className="db-station">
                          {v.next_booking.station_name || "—"}
                          {v.next_booking.slot_code ? ` · Slot ${v.next_booking.slot_code}` : ""}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="detail-grid">
                    <div><em>SOC</em><b>{v.soc != null ? `${v.soc}%` : "—"}</b></div>
                    <div><em>Speed</em><b>{v.speed_kmh != null ? `${v.speed_kmh} km/h` : "—"}</b></div>
                    <div><em>Odometer</em><b>{v.odo != null ? `${v.odo.toLocaleString()} km` : "—"}</b></div>
                    <div><em>State</em><b>{v.state || "—"}</b></div>
                    <div><em>GPS</em><b>{fmtTime(v.gps_ts)}</b></div>
                    <div><em>Connection</em><b>{v.connection_info || (v.connected ? "Connected" : "Disconnected")}</b></div>
                  </div>
                </div>
              );
            })()}
          </main>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    // /api/auth/me returns the user object at the TOP level ({id, email,
    // name, ...}); only the login endpoint wraps it ({ok, user}). The old
    // `d.user` read was always undefined here, which silently logged
    // everyone out on every page refresh.
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setUser(d ? d.user || (d.id ? d : null) : null))
      .catch(() => setUser(null));
  }, []);

  const logout = useCallback(async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch {}
    setUser(null);
  }, []);

  if (user === undefined) return <div className="track-boot">Loading…</div>;
  if (!user) return <Login onLogin={(u) => setUser(u)} />;
  return <Dashboard user={user} onLogout={logout} />;
}
